import { containsExecutableMarkup } from "./inputValidation";

/**
 * Enterprise-Grade Global Input Validator & Submission Guard
 *
 * Requirements:
 * 1. Does NOT automatically correct, strip, or erase user input while typing or pasting.
 * 2. Provides real-time visual feedback: adds red border, error styling, and inline message.
 * 3. Prevents form submission and action triggering until all invalid inputs are resolved.
 * 4. Works seamlessly with React state management and browser DevTools tamper-resistance.
 */

let isInitialized = false;

// Helper to show/hide inline security error message
function updateSecurityErrorFeedback(element, isInvalid) {
  if (!element || !element.parentElement) return;

  const parent = element.parentElement;
  let errorEl = parent.querySelector(".security-error-msg");

  if (isInvalid) {
    element.classList.add("security-invalid");
    element.classList.add("input-error");
    element.setAttribute("aria-invalid", "true");
    if (typeof element.setCustomValidity === "function") {
      element.setCustomValidity(
        "Invalid input: script tags and HTML markup are not allowed.",
      );
    }

    // Only create security badge if no React error message is already present
    const hasExistingError = parent.querySelector(
      ".text-danger, .error-feedback, small, .error-message",
    );
    if (!errorEl && !hasExistingError) {
      errorEl = document.createElement("div");
      errorEl.className = "security-error-msg text-danger small mt-1";
      errorEl.setAttribute("role", "alert");
      errorEl.style.color = "#dc2626";
      errorEl.style.fontSize = "12px";
      errorEl.style.marginTop = "4px";
      errorEl.style.fontWeight = "500";
      errorEl.textContent =
        "Contains invalid script or HTML tags. Please remove them.";
      parent.appendChild(errorEl);
    } else if (errorEl) {
      errorEl.textContent =
        "Contains invalid script or HTML tags. Please remove them.";
      errorEl.style.display = "block";
    }

    // Mark parent form or container
    const form = element.closest("form") || element.closest(".modal") || element.closest(".patient-page");
    if (form) {
      form.setAttribute("data-has-security-errors", "true");
    }
  } else {
    element.classList.remove("security-invalid");
    element.classList.remove("input-error");
    element.removeAttribute("aria-invalid");
    if (typeof element.setCustomValidity === "function") {
      element.setCustomValidity("");
    }

    if (errorEl) {
      errorEl.remove();
    }

    // Check if form has any other security errors
    const form = element.closest("form") || element.closest(".modal") || element.closest(".patient-page");
    if (form) {
      const remainingErrors = form.querySelectorAll(".security-invalid");
      if (remainingErrors.length === 0) {
        form.removeAttribute("data-has-security-errors");
      }
    }
  }
}

function inspectInputElement(target) {
  if (!target) return;
  const isInput =
    target instanceof HTMLInputElement &&
    !["file", "checkbox", "radio", "button", "submit", "reset"].includes(
      target.type,
    );
  const isTextArea = target instanceof HTMLTextAreaElement;

  if (!isInput && !isTextArea) return;

  const value = target.value;
  if (typeof value === "string") {
    const isInvalid = containsExecutableMarkup(value);
    updateSecurityErrorFeedback(target, isInvalid);
  }
}

function handleInput(e) {
  const target = e.target;
  if (!target) return;
  inspectInputElement(target);
}

function handlePaste(e) {
  const target = e.target;
  if (!target) return;

  // Let the paste happen naturally so the user sees their text,
  // then inspect immediately after the DOM is updated
  setTimeout(() => {
    inspectInputElement(target);
  }, 0);
}

function handleDrop(e) {
  const target = e.target;
  if (!target) return;

  setTimeout(() => {
    inspectInputElement(target);
  }, 0);
}

// Intercept form submissions if any input in the form contains executable markup
function handleSubmit(e) {
  const form = e.target;
  if (!form || typeof form.querySelectorAll !== "function") return;

  const inputs = form.querySelectorAll("input, textarea");
  let hasViolation = false;
  let firstInvalid = null;

  for (const input of inputs) {
    if (
      typeof input.value === "string" &&
      containsExecutableMarkup(input.value)
    ) {
      hasViolation = true;
      updateSecurityErrorFeedback(input, true);
      if (!firstInvalid) firstInvalid = input;
    }
  }

  if (hasViolation || form.getAttribute("data-has-security-errors") === "true") {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (firstInvalid && typeof firstInvalid.focus === "function") {
      firstInvalid.focus();
    }
    console.warn(
      "[Security Guard] Form submission blocked: Unresolved script or HTML markup detected.",
    );
  }
}

// Intercept button clicks (even if disabled attribute was removed via DevTools inspect)
function handleClick(e) {
  const target = e.target;
  if (!target) return;

  const button = target.closest("button, input[type='submit']");
  if (!button) return;

  // Check enclosing form or container
  const container =
    button.closest("form") ||
    button.closest(".modal") ||
    button.closest(".modal-content") ||
    button.closest(".patient-page") ||
    document;

  const inputs = container.querySelectorAll("input, textarea");
  let hasViolation = false;
  let firstInvalid = null;

  for (const input of inputs) {
    if (
      typeof input.value === "string" &&
      containsExecutableMarkup(input.value)
    ) {
      hasViolation = true;
      updateSecurityErrorFeedback(input, true);
      if (!firstInvalid) firstInvalid = input;
    }
  }

  if (hasViolation) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (firstInvalid && typeof firstInvalid.focus === "function") {
      firstInvalid.focus();
    }
    console.warn(
      "[Security Guard] Action trigger blocked: Element contains unresolved script or HTML markup.",
    );
  }
}

export function initGlobalInputSanitizer() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  if (isInitialized) return;
  isInitialized = true;

  // Real-time validation without auto-correcting/mutating inputs
  document.addEventListener("input", handleInput, true);
  document.addEventListener("change", handleInput, true);
  document.addEventListener("paste", handlePaste, true);
  document.addEventListener("drop", handleDrop, true);

  // Capture-phase block against form submissions & button clicks with unresolved errors
  document.addEventListener("submit", handleSubmit, true);
  document.addEventListener("click", handleClick, true);

  console.info(
    "[Security Guard] Enterprise Visual Validation & Anti-Tamper Guard initialized.",
  );
}

export default initGlobalInputSanitizer;
